import { AuthenticatedHttpClient } from "@/lib/axios";
import { PaginatedResponse } from "@/types/common";
import {
  TemplateCheckResponse,
  TemplateReport,
  TemplateType,
} from "@/types/template.types";
import { AxiosResponse } from "axios";

export class TemplateService {
  private http: AuthenticatedHttpClient;

  constructor(httpClient: AuthenticatedHttpClient) {
    this.http = httpClient;
  }

  async create({
    templateData,
    report,
  }: {
    templateData: Partial<TemplateType>;
    report?: TemplateCheckResponse;
  }): Promise<AxiosResponse<TemplateType>> {
    return await this.http.post("/template", {
      templateData,
      report,
    });
  }

  async getAll(params?: {
    page?: number;
    limit?: number;
    labels?: string[];
  }): Promise<PaginatedResponse<TemplateType>> {
    const response = await this.http.post(
      "/template/all?page=" + params?.page + "&limit=" + params?.limit,
      {
        labels: params?.labels,
      }
    );
    return response as unknown as PaginatedResponse<TemplateType>;
  }

  async getById(id: string): Promise<TemplateType> {
    return (await this.http.get(`/template/${id}`)) as unknown as TemplateType;
  }

  async search(searchTerm?: string) {
    searchTerm = searchTerm?.trim();
    const url = searchTerm
      ? `/template/search?searchTerm=${searchTerm}`
      : `/template/search`;
    const response = await this.http.get(url);
    return response as unknown as TemplateType[];
  }

  async getAllCompanyTemplates(): Promise<TemplateType[]> {
    const response = await this.http.get("/template/all-company");
    return response as unknown as TemplateType[];
  }

  async update<T>(
    id: string,
    templateData: Partial<TemplateType>
  ): Promise<AxiosResponse<T>> {
    return await this.http.put(`/template/${id}`, templateData);
  }

  async delete<T>(id: string): Promise<AxiosResponse<T>> {
    return await this.http.delete(`/template/${id}`);
  }

  async check(
    templateData: Partial<TemplateType>
  ): Promise<TemplateCheckResponse> {
    return (await this.http.post(
      "/template/check",
      templateData
    )) as unknown as TemplateCheckResponse;
  }

  async getReport(id: string): Promise<TemplateReport> {
    return (await this.http.get(
      `/template/${id}/report`
    )) as unknown as TemplateReport;
  }

  async addLabelsToTemplates(
    templateIds: string[],
    labelIds: string[]
  ): Promise<AxiosResponse> {
    return await this.http.put("/template/labels/bulk", {
      templateIds,
      labelIds,
    });
  }

  async removeLabelsFromTemplates(
    templateIds: string[],
    labelIds: string[]
  ): Promise<AxiosResponse> {
    return await this.http.put("/template/labels/bulk-remove", {
      templateIds,
      labelIds,
    });
  }
}
